import type { ExecutionMetadata } from './execution-metadata'
import { VERSION } from './version'

export { VERSION } from './version'

export const PACKAGE = '@zerohuman/shared' as const

/** Root package name. Sentry releases and MCP clientInfo use this, not `@zerohuman/shared`. */
export const PRODUCT = 'zerohuman-os' as const

export function productRelease(): string {
    return `${PRODUCT}@${VERSION}`
}

export type {
    AgentConnection,
    AgentConnectionsResponse,
    AgentConnectionTarget,
    AgentProvider,
    AgentSubscription,
    AgentSubscriptionPlan,
    AgentUsage,
    AgentUsageBalance,
    AgentUsageByConnection,
    AgentUsageDay,
    AgentUsageKind,
    AgentUsageQuota,
} from './agents'
export {
    AGENT_CONNECTION_DESCRIPTION_MAX,
    AGENT_CONNECTION_NAME_MAX,
    AGENT_CONNECTION_USAGE_IDS_MAX,
    AGENT_PROVIDER_SUBSCRIPTION_PLANS,
    AGENT_PROVIDERS,
    AGENT_SUBSCRIPTION_PLANS,
    AGENT_SUBSCRIPTION_PRICES,
    isAgentProvider,
} from './agents'
export {
    BINDING_PROP_KEY,
    type BindingProps,
    GIT_PROPS,
    hasGitProps,
    MAX_BINDING_PROP_VALUE,
    MAX_BINDING_PROPS,
} from './binding-props'
export * from './built-in-tools'
export {
    childExecutionMetadata,
    type ExecutionMetadata,
    type JsonValue,
    mergeExecutionMetadata,
    metadataAfterReturnEdge,
    type ReturnTarget,
} from './execution-metadata'
export {
    AI_ACCOUNT_ABANDON_REASONS,
    type AiAccountAbandonReason,
    FUNNEL_EVENTS,
    FUNNEL_FORM_FIELDS,
    FUNNEL_FORM_FLOWS,
    FUNNEL_MEASURES,
    FUNNEL_METRICS,
    FUNNEL_NEEDS_YOU_ITEM_TYPES,
    type FunnelEmitter,
    type FunnelEventEntry,
    type FunnelMeasure,
    type FunnelMetricDefinition,
    type FunnelMetricId,
    type FunnelPayloadField,
    funnelCoverageGaps,
    funnelEvent,
    funnelMetricCollisions,
    funnelMetricGaps,
    SETUP_PATHS,
    type SetupPath,
    USER_KINDS,
    type UserKind,
} from './funnel'
export {
    describeKpiBoard,
    type KpiBoard,
    type KpiRag,
    type KpiReadingView,
    NO_KEY_KPI,
    type TeamKpiBoardRow,
} from './kpi'
export { escapedMarkdown, frontmatter, markdownCell } from './markdown'
export {
    DEFAULT_CONSTELLATION_LIMIT,
    MAX_CONSTELLATION_LIMIT,
    type MemoryConstellationLink,
    type MemoryConstellationResponse,
    type MemoryConstellationUnit,
    type MemoryLinkType,
    type MemoryUnitType,
} from './memory-constellation'
export {
    type MemoryCorrectionState,
    type MemoryFilterableNode,
    type MemoryGraphEdge,
    type MemoryGraphQuery,
    type MemoryKnowledgeType,
    type MemoryLabel,
    type MemoryNodeOrigin,
    type MemoryQueryableNode,
    type MemoryRelationshipType,
    matchesMemoryQuery,
    matchesMemorySearch,
    memoryNodeHasLabel,
    memoryNodeInTimeWindow,
} from './memory-graph'
export {
    type MemoryRecallHealth,
    memoryRecallHealth,
    RECALL_HEALTH_WINDOW_HOURS,
    RECALL_UNAVAILABLE_TARGET,
} from './memory-recall-health'
export { hasMetrics, metricList, metricsText } from './metrics'
export {
    type MustGateEntry,
    type MustGateMatch,
    type MustGateMatchValue,
    mustGateEntryLabel,
    mustGateEntryMatch,
    mustGateEntryTool,
} from './must-gate'
export { RUN_STATUSES, type RunStatus } from './run-status'
export type { RunCost, SpendBilling } from './spend'
export { USAGE_UNITS, type UsageUnit } from './usage-unit'
export { classifyUserKind, type TechnicalAnswer, technicalAnswerOf, type UserKindSignals } from './user-kind'
export type { WebhookDetail, WebhookKind, WebhookStatus, WebhookSummary } from './webhooks'
export {
    CREATE_TASK_CHAT_PURPOSE,
    closestRoleFromTyped,
    type KnownWorkRole,
    OTHER_ROLE,
    type QuickReply,
    quickRepliesFor,
    REPORTING_STYLES,
    reportingStyleFor,
    roleAnswerFromTyped,
    SETUP_CHAT_PURPOSE,
    type SetupQuestion,
    SOMETHING_ELSE,
    SOMETHING_ELSE_LABEL,
    setupAnswerFromChip,
    setupAnswerFromTyped,
    setupQuestion,
    setupQuickReplies,
    TECHNICAL_LEVEL_LABELS,
    TECHNICAL_LEVELS,
    type TechnicalLevel,
    WORK_PROFILE_TEXT_MAX,
    WORK_PROFILE_WORK_FIRST,
    WORK_PROFILE_WORK_FIRST_TEXT,
    WORK_ROLE_LABELS,
    WORK_ROLES,
    type WorkChoice,
    type WorkProfile,
    type WorkRole,
    workAnswerFromTyped,
} from './work-profile'

export const ACTIVE_RUN_STATUSES = [
    'queued',
    'running',
    'waiting_on_gate',
    'waiting_on_run',
    'paused_spend',
    'paused_plan',
    'paused_team',
] as const

export type ActiveRunStatus = (typeof ACTIVE_RUN_STATUSES)[number]

export function isActiveRunStatus(status: string): status is ActiveRunStatus {
    return (ACTIVE_RUN_STATUSES as readonly string[]).includes(status)
}

/** Unfinished runs in these statuses are owned by Blockers, so Home's live-runs count leaves them out. */
export const BLOCKER_OWNED_RUN_STATUSES = ['blocked', 'paused_spend', 'paused_plan'] as const

/**
 * Unfinished runs that are not underway: the ones Blockers owns, a run held because its team is switched off
 * (#1809), and a run held by a person's pause of its execution (`paused`, #809). Blockers lists neither of the last
 * two: switching a team off is the enterprise's own choice, and a pause waits on the person who can resume it.
 * Home's live-runs count and the `live` run filter leave all of them out.
 */
export const NOT_LIVE_RUN_STATUSES = [...BLOCKER_OWNED_RUN_STATUSES, 'paused_team', 'paused'] as const

export function isLiveRun(run: { finishedAt: string | Date | null; status: string }): boolean {
    return run.finishedAt === null && !(NOT_LIVE_RUN_STATUSES as readonly string[]).includes(run.status)
}

export const TASK_ORIGINS = ['tenant', 'os'] as const

export type TaskOrigin = (typeof TASK_ORIGINS)[number]

export function isOsTask(task: { origin?: string | null }): boolean {
    return task.origin === 'os'
}

/**
 * The tiers a task may ask for, cheapest first. `economical` is the canonical name of the cheapest tier
 * (#664 item 4): "fast" read as "quickest" when what it means is "cheapest", so the written name now matches
 * the label the portal always showed. Rows written before the rename still say `fast`; see
 * `MODEL_TIER_ALIASES`.
 */
export const MODEL_TIERS = ['economical', 'mid', 'frontier'] as const

export type ModelTier = (typeof MODEL_TIERS)[number]

/**
 * Pre-rename spellings still found in stored rows (#664 item 4). Read paths accept them; no write path emits
 * them, so this map only ever shrinks — once no `fast` rows remain it can go.
 */
export const MODEL_TIER_ALIASES: Readonly<Record<string, ModelTier>> = { fast: 'economical' }

/**
 * The tier a stored or submitted value names, or null when it names none. Accepts the canonical tiers and the
 * back-compat aliases above, so a task version written before the rename resolves exactly as it did.
 */
export function canonicalModelTier(tier: string | null | undefined): ModelTier | null {
    if (typeof tier !== 'string') {
        return null
    }
    const value = tier.trim()
    const alias = MODEL_TIER_ALIASES[value]
    if (alias) {
        return alias
    }
    return (MODEL_TIERS as readonly string[]).includes(value) ? (value as ModelTier) : null
}

/**
 * What a caller may send as a task's model to say "choose one for me". It is never stored: a task that names no
 * model is the one whose model is chosen for it.
 */
export const AUTO_MODEL = 'auto'

/** The model a task names outright, or null when it names none: unset, blank, or `auto`. */
export function pinnedModel(model: string | null | undefined): string | null {
    const named = typeof model === 'string' ? model.trim() : ''
    return named === '' || named.toLowerCase() === AUTO_MODEL ? null : named
}

/** A tier arriving over the wire or out of a task definition is free text; only a real one means anything. */
export function isModelTier(value: string | null | undefined): value is ModelTier {
    return typeof value === 'string' && (MODEL_TIERS as readonly string[]).includes(value)
}

/**
 * How much of the enterprise's memory a chat reply reads, smallest first. Each depth is a cap, in tokens, on what
 * one recall may bring back for one message. Deeper reads further down the same ranking, so it finds more and
 * costs more: recalled notes stay in the conversation and are sent to the model on every later step.
 */
export const CHAT_MEMORY_DEPTHS = ['standard', 'large', 'extended'] as const

export type ChatMemoryDepth = (typeof CHAT_MEMORY_DEPTHS)[number]

/** What an enterprise that has chosen nothing gets. */
export const DEFAULT_CHAT_MEMORY_DEPTH: ChatMemoryDepth = 'standard'

/** The recall cap each depth sets, in tokens. The deepest is the most the OS allows. */
export const CHAT_MEMORY_DEPTH_TOKENS: Readonly<Record<ChatMemoryDepth, number>> = {
    standard: 4096,
    large: 8192,
    extended: 16_384,
}

/** A depth arriving over the wire is free text; only a real one means anything. */
export function isChatMemoryDepth(value: string | null | undefined): value is ChatMemoryDepth {
    return typeof value === 'string' && (CHAT_MEMORY_DEPTHS as readonly string[]).includes(value)
}

/** The recall cap for a stored depth. A value that names none reads as the default, never as no cap. */
export function chatMemoryDepthTokens(depth: string | null | undefined): number {
    return CHAT_MEMORY_DEPTH_TOKENS[isChatMemoryDepth(depth) ? depth : DEFAULT_CHAT_MEMORY_DEPTH]
}

/** An enterprise's chat memory depth as its setting reads: the choice, and every depth with the cap it sets. */
export type ChatMemoryDepthSetting = {
    chatMemoryDepth: ChatMemoryDepth
    depths: { depth: ChatMemoryDepth; maxTokens: number }[]
}

/** The setting for a stored depth. A value that names none reads as the default. */
export function chatMemoryDepthSetting(stored: string | null | undefined): ChatMemoryDepthSetting {
    return {
        chatMemoryDepth: isChatMemoryDepth(stored) ? stored : DEFAULT_CHAT_MEMORY_DEPTH,
        depths: CHAT_MEMORY_DEPTHS.map(depth => ({ depth, maxTokens: CHAT_MEMORY_DEPTH_TOKENS[depth] })),
    }
}

/** Lower rank is cheaper. Unknown tiers sit at `mid`. */
export function modelTierRank(tier: string): number {
    const canonical = canonicalModelTier(tier)
    return canonical === null ? 1 : MODEL_TIERS.indexOf(canonical)
}

export type TaskInputField = {
    key: string
    label: string
    /**
     * `file` (os#679): a file the task is given. The start body carries it as a `data:<type>;base64,` URL; the run
     * keeps it as a run file and its stored input holds only `TaskInputFile`. A `file` input takes no `default`.
     *
     * `link` (#1754): a web address, held as text. A start's value has to be an absolute http(s) URL (the API's
     * start-input check refuses anything else); the task form names it Link and the run-start form gives it a URL field.
     */
    type: 'string' | 'number' | 'boolean' | 'file' | 'link'
    required?: boolean
    placeholder?: string
    default?: string | number | boolean
}

/**
 * An enterprise's own values for a task's inputs (F2): one value per declared input key, kept on the task row beside
 * its schedule rather than on a version, so the enterprise that runs a task, its own or a linked clone of another
 * enterprise's, sets them without editing the definition. Every start takes them, under its own input and over the
 * version's defaults. A `file` input takes none: a file is given at each start.
 */
export type TaskInputValues = Record<string, string | number | boolean>

/**
 * What a `file` input holds once its run has started (os#679): the run file's handle and what is known about it,
 * never its bytes. This is what `runs.input_json` stores and what the model is shown; an `upload` names `file`.
 */
export type TaskInputFile = {
    file: string
    name: string
    size: number
    contentType: string
}

/** Next run to start when this run finishes. Stored on the task version. */
export type TaskSuccessor = {
    slug: string
    /** `succeeded` / `failed` / `terminal`, or a named outcome from the run result (`outcome` JSON). */
    on?: string
    /**
     * A condition on the input the successor would receive: `has_issue`, `has_pr`, `no_pr`, `has_work_item` or
     * `no_work_item`. Input keys only, never a word read off a skill.
     */
    when?: string
    /** Loop cap: most runs of `slug` this edge may start for one issue, PR or work item before escalating. Default `SUCCESSOR_MAX_RUNS_PER_ISSUE`. */
    maxRunsPerIssue?: number
    /** Start the successor this many minutes later (e.g. re-check CI while it is still running). */
    delayMinutes?: number
    /** At least one named mise task must have the required exit code in this run. Missing evidence blocks the edge. */
    verificationAnyOf?: Array<{ task: string; exitCodes: number[] }>
    /** Stop at a visible escalation blocker for this task instead of starting it. */
    escalate?: boolean
    /** Generic metadata merged into the successor execution context. */
    metadata?: ExecutionMetadata
    /**
     * Continue the deciding run's own conversation instead of starting the successor fresh (os#235), when this
     * edge's `on` is a CEO decision outcome (`ceo_approved`/`ceo_rejected`/`ceo_changes_requested`). Opt-in per
     * edge: unset or false is exactly today's behaviour (a fresh run seeded from `ceoNote`/`ceoAnswers` only),
     * and it is ignored on every other `on` value.
     */
    resumeConversation?: boolean
    /**
     * Sends this edge's outcome to whoever asked, instead of naming a task directly (issue #784). `self` names
     * the task that fired this edge; any other value is a task slug of this edge's own choosing. The task it
     * starts reads it back with a `$return` successor on its own graph — see `docs/execution-metadata.md`.
     */
    returnTo?: string
}

export type ListQuery = {
    page: number
    limit: number
    search: string
    sort: string
    order: 'ASC' | 'DESC'
    filters: Record<string, string>
}

export type ListResponse<T> = {
    data: T[]
    total: number
    page: number
    limit: number
    pages: number
}

/** The mail host every member address lives on; each enterprise gets its own subdomain of it. */
export const MEMBER_MAIL_HOST = 'zerohuman.com' as const

/** The one domain a member's own enterprise sends and receives mail on: `{slug}.zerohuman.com`. */
export function memberMailDomain(slug: string): string {
    return `${slug}.${MEMBER_MAIL_HOST}`
}

/**
 * Member email for a (full) name at `domain`: lower case, accents dropped, spaces and dashes become dots.
 * `Mary-Jane van Dyke` at `os.zerohuman.com` is `mary.jane.van.dyke@os.zerohuman.com`.
 */
export function memberEmail(name: string, domain: string): string {
    const local = name
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, '.')
        .replace(/[^a-z0-9._]/g, '')
        .replace(/\.{2,}/g, '.')
        .replace(/^\.|\.$/g, '')
    return `${local}@${domain}`
}

export {
    CronError,
    type CronField,
    cronError,
    cronMatches,
    isValidTimezone,
    nextCronFire,
    type ParsedCron,
    parseCron,
    type WallClock,
    wallClock,
} from './cron'
export type {
    BoardLane,
    ExecutionBoard,
    ExecutionCloseCompletedResult,
    ExecutionCloseRefusal,
    ExecutionContinuation,
    ExecutionCounts,
    ExecutionDetail,
    ExecutionLabel,
    ExecutionLane,
    ExecutionLink,
    ExecutionMember,
    ExecutionPauseEntry,
    ExecutionResource,
    ExecutionRole,
    ExecutionRun,
    ExecutionStatus,
    ExecutionSummary,
    ExecutionTaskEdge,
    ExecutionTaskNode,
    ExecutionTaskRef,
    WorkItemCard,
    WorkItemOrigin,
} from './executions'
export {
    BOARD_LANE_LIMIT_MAX,
    BOARD_LANE_LIMIT_PARAM,
    BOARD_LANES,
    boardLanePageParam,
    EXECUTION_CLOSE_REFUSALS,
    EXECUTION_LANE_BY_STATUS,
    EXECUTION_LANES,
    EXECUTION_OPEN_BY_STATUS,
    EXECUTION_STATUSES,
    executionCloseRefusal,
    executionIsOpen,
    executionLane,
    NO_OUTCOME_NAMED,
    runLane,
} from './executions'
export {
    KPI_SOURCE_KINDS,
    type KpiDirection,
    type KpiOverdue,
    type KpiReading,
    type KpiSourceKind,
    type KpiTarget,
    type KpiTrend,
    kpiLight,
    kpiOverdue,
    kpiReadingOverdue,
    kpiTrend,
    sortReadingsNewestFirst,
} from './kpi'
export {
    aggregateMemoryEvalScore,
    compareMemoryEvalAnswers,
    hadNoProbes,
    labelMemoryEvalDelta,
    type MemoryEvalAnswer,
    type MemoryEvalQuestion,
    type MemoryEvalQuestionResult,
    type MemoryEvalRegressionLabel,
    type MemoryEvalRun,
    type MemoryEvalRunConfig,
    type MemoryEvalRunDetail,
    type MemoryEvalRunSnapshot,
    type MemoryEvalRunSummary,
    regressedQuestions,
} from './memory-eval'
export {
    MEMORY_EVAL_PROBE_CUE_WORDS,
    MEMORY_EVAL_PROBE_FACT_WORDS,
    MEMORY_EVAL_PROBE_MIN_FACT_WORDS,
    MEMORY_EVAL_PROBE_TEMPLATE_VERSION,
    MEMORY_EVAL_PROBE_TEMPLATES,
    MEMORY_EVAL_PROBES_PER_ENTERPRISE,
    type MemoryEvalProbe,
    type MemoryEvalProbeEntryKind,
    type MemoryEvalProbeSource,
    type MemoryEvalProbeTemplate,
    memoryEvalProbeFor,
    selectMemoryEvalProbes,
} from './memory-eval-questions'
export {
    DEFAULT_SCHEDULE_TIMEZONE,
    describeCron,
    describeSchedule,
    FOLLOW_ENTERPRISE_TIMEZONE,
    nextScheduleFire,
    parseSchedule,
    resolveScheduleTimezone,
    SCHEDULE_PRESETS,
    type ScheduleSkip,
    scheduleError,
    type TaskSchedule,
} from './schedule'

/**
 * Whether a task may start new runs right now: `active` (the default) or `disabled`. Not the task's lifecycle `status`
 * (`active | deprecated`) and not a run's `RunStatus`. Stored on the task row as `run_status`, shown as `runStatus`.
 */
export const TASK_RUN_STATUSES = ['active', 'disabled'] as const

export type TaskRunStatus = (typeof TASK_RUN_STATUSES)[number]

/**
 * Whether `value` is exactly one of `TASK_RUN_STATUSES`. No trimming and no case folding: a write boundary refuses
 * anything else rather than coercing it, so a mistyped `ACTIVE` never reads as a switch that was set.
 */
export function isTaskRunStatus(value: unknown): value is TaskRunStatus {
    return typeof value === 'string' && (TASK_RUN_STATUSES as readonly string[]).includes(value)
}

export {
    AI_ACCOUNT_CHOICES,
    AI_ACCOUNT_KEY_FOR,
    type AiAccountChoice,
    type AiAccountKeyId,
    type AiAccountPlanId,
    orList,
    PROVIDER_KEY_IN_CHAT_TEXT,
} from './ai-account-choices'
export { AI_ACCOUNT_NOTICE, type AiAccountNotice } from './ai-account-notice'
export {
    AUTH_SCREEN_COPY,
    AUTH_SCREEN_LINKS,
    type AuthFooterPart,
    type AuthScreenCopy,
    authFooterParts,
} from './auth-screen-copy'
export * from './built-in-tasks'
export { type CallLimit, MAX_CALL_LIMITS } from './call-limits'
export { CHARTER_QUESTIONS } from './charter'
export { CHAT_DETAILS_MARKER } from './chat-details'
export {
    CHAT_FILE_CHARS_PER_TOKEN,
    CHAT_FILE_COST_NOTE_MIN_CHARS,
    CHAT_FILE_COST_NOTE_MIN_TOKENS,
    type ChatFileCostInput,
    type ChatFileCostProvider,
    chatFileCostNote,
} from './chat-file-cost'
export {
    chatFileKindLabel,
    chatFileMediaType,
    isChatFilePreviewable,
    OPAQUE_CHAT_FILE_MEDIA_TYPE,
} from './chat-file-labels'
export {
    CHAT_FILE_LIMITS,
    type ChatFileKind,
    type ChatFileLimits,
    chatFileKind,
    chatFileLimitsSentence,
    chatFilesPerMessage,
    formatChatFileSize,
    isSupportedChatFile,
    supportedChatFileKindsSentence,
} from './chat-file-limits'
export {
    CHAT_STEP_LIMITS,
    CHAT_STEP_STATES,
    type ChatStep,
    type ChatStepLimits,
    type ChatStepState,
    isChatStepState,
} from './chat-step-limits'
export {
    CHAT_UNPROMPTED_TURN_LIMITS,
    type ChatUnpromptedTurnLimits,
    chatUnpromptedTurnLimitChoices,
    chatUnpromptedTurnLimitInForce,
    chatUnpromptedTurnLimitLabel,
    isChatUnpromptedTurnLimit,
} from './chat-unprompted-turn-limit'
export { type DomainSendingWait, domainSendingWaitText } from './domain-sending'
export { checkEmailAddress, EMAIL_INVALID_MESSAGE, type EmailAddressCheck } from './email-address'
export * from './enterprise-billing-alert'
export {
    AI_ACCOUNT_DROP_OFF_REPORT,
    type AiAccountDropOffReport,
    type AiAccountPathColumn,
    type AiAccountReasonColumn,
    aiAccountPathColumn,
    aiAccountReasonColumn,
    londonWeekStart,
    londonWeekWindow,
} from './funnel'
export * from './included-model-claims'
export {
    MAIL_ATTACHMENT_LIMITS,
    type MailAttachmentLimits,
    mailAttachmentLimitsSentence,
} from './mail-attachment-limits'
export {
    MAIL_CONTACTS_IMPORT_LIMITS,
    type MailContactsImportLimits,
    mailContactsImportLimitsSentence,
} from './mail-contacts-import-limits'
export {
    isMailboxActionSurface,
    MAILBOX_ACTION_MAIL_INPUT,
    MAILBOX_ACTION_SCHEDULE_REFUSALS,
    MAILBOX_ACTION_SURFACES,
    MAX_MAILBOX_ACTION_LABEL,
    type MailboxAction,
    type MailboxActionSchedule,
    type MailboxActionSurface,
} from './mailbox-actions'
export * from './onboarding-copy'
export * from './payment-notice'
export * from './plan-limit-notice'
export {
    REALTIME_CLOSE_CODES,
    REALTIME_PATH,
    REALTIME_PING_INTERVAL_MS,
    type RealtimeFrame,
    type RealtimePresence,
} from './realtime/sockets'
export { REALTIME_TOPICS, type Topic } from './realtime/topics'
export {
    type CanonicalToolEntry,
    canonicalToolEntries,
    type ToolEntry,
    toolEntryName,
    toolEntryNames,
    toolEntryOptional,
    unreadableToolEntry,
} from './tool-entries'
export {
    declaredSlotKeys,
    fillToolEntries,
    fillToolName,
    MAX_SLOT_ACTIONS,
    MAX_TOOL_SLOT_ACTION_DESCRIPTION,
    MAX_TOOL_SLOT_ACTIONS,
    MAX_TOOL_SLOT_DESCRIPTION,
    MAX_TOOL_SLOT_LABEL,
    MAX_TOOL_SLOTS,
    namedSlotActions,
    RESERVED_TOOL_SLOT_KEYS,
    repoListed,
    SLOT_ACTION_TARGET,
    type SlotActionMap,
    type SlotActionMaps,
    type SlotNamingFields,
    slotActionAliases,
    TOOL_SLOT_ACTION,
    TOOL_SLOT_KEY,
    type ToolNamingFields,
    type ToolSlot,
    type ToolSlotAction,
    toolNamePrefix,
    toolSlotKeyProblem,
    undeclaredSlotActions,
    unmappedSlotActions,
    withFilledSlots,
} from './tool-slots'
